import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { CalendarIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const SEPT_START = new Date(2026, 8, 1);
const SEPT_END = new Date(2026, 8, 30);

const formatDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

const toISO = (date) => {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${m}-${d}`;
};

const SectionTitle = ({ children }) => (
  <h3 className="font-serif text-xl font-semibold blue-text border-b-2 pb-2 mt-2" style={{ borderColor: '#D4AF37' }}>
    {children}
  </h3>
);

const DatePickerField = ({ value, onChange, label, testId }) => {
  const { t } = useLanguage();
  return (
    <div>
      <Label className="blue-text">{label} *</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            data-testid={testId}
            className="w-full justify-start text-left font-normal border-2 border-primary blue-text mt-1 cream-bg hover:bg-blue-50"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? formatDate(value) : t('travelForm.pickDate')}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 cream-bg border-2 border-primary" align="start">
          <Calendar
            mode="single"
            selected={value ? new Date(value + 'T00:00:00') : undefined}
            onSelect={(date) => date && onChange(toISO(date))}
            defaultMonth={SEPT_START}
            fromMonth={SEPT_START}
            toMonth={SEPT_END}
            disabled={(date) => date < SEPT_START || date > SEPT_END}
            disableNavigation
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

const FlightSection = ({ index, person, set, label, prefix }) => {
  const { t } = useLanguage();
  const airport = person[`${prefix}Airport`];
  return (
    <div className="space-y-3 p-4 border border-primary rounded-sm">
      <p className="font-sans font-semibold blue-text tracking-wide uppercase text-sm">{label}</p>
      <div>
        <Label className="blue-text">{t('travelForm.airport')} *</Label>
        <Select value={airport} onValueChange={(v) => set(`${prefix}Airport`, v)}>
          <SelectTrigger className="border-2 border-primary cream-bg blue-text mt-1" data-testid={`${prefix}-airport-select-${index}`}>
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tivat">{t('travelForm.airportTivat')}</SelectItem>
            <SelectItem value="podgorica">{t('travelForm.airportPodgorica')}</SelectItem>
            <SelectItem value="other">{t('travelForm.airportOther')}</SelectItem>
          </SelectContent>
        </Select>
        {airport === 'other' && (
          <Input
            value={person[`${prefix}AirportOther`]}
            onChange={(e) => set(`${prefix}AirportOther`, e.target.value)}
            placeholder={t('travelForm.airportOtherPlaceholder')}
            className="border-2 border-primary cream-bg mt-2"
            data-testid={`${prefix}-airport-other-input-${index}`}
          />
        )}
      </div>
      <DatePickerField
        value={person[`${prefix}Date`]}
        onChange={(v) => set(`${prefix}Date`, v)}
        label={t('travelForm.date')}
        testId={`${prefix}-date-btn-${index}`}
      />
      <div>
        <Label className="blue-text">{t('travelForm.time')} *</Label>
        <Input
          type="time"
          value={person[`${prefix}Time`]}
          onChange={(e) => set(`${prefix}Time`, e.target.value)}
          className="border-2 border-primary cream-bg mt-1"
          data-testid={`${prefix}-time-input-${index}`}
        />
      </div>
      <div>
        <Label className="blue-text">{t('travelForm.flight')} *</Label>
        <Input
          value={person[`${prefix}Flight`]}
          onChange={(e) => set(`${prefix}Flight`, e.target.value)}
          placeholder={t('travelForm.flightPlaceholder')}
          className="border-2 border-primary cream-bg mt-1"
          data-testid={`${prefix}-flight-input-${index}`}
        />
      </div>
    </div>
  );
};

export const TravelPersonForm = ({ index, person, firstPersonName, onChange }) => {
  const { t } = useLanguage();

  const set = (field, value) => onChange(index, { ...person, [field]: value });

  const toggleList = (field, value) => {
    const list = person[field] || [];
    set(field, list.includes(value) ? list.filter(v => v !== value) : [...list, value]);
  };

  const allergyOptions = [
    ['lactose', t('travelForm.allergyLactose')],
    ['gluten', t('travelForm.allergyGluten')],
    ['citrus', t('travelForm.allergyCitrus')],
    ['nuts', t('travelForm.allergyNuts')],
    ['red_fruits', t('travelForm.allergyRedFruits')],
    ['other', t('travelForm.allergyOther')]
  ];

  const drinkOptions = [
    ['red_wine', t('travelForm.drinkRedWine')],
    ['white_wine', t('travelForm.drinkWhiteWine')],
    ['rose', t('travelForm.drinkRose')],
    ['sparkling', t('travelForm.drinkSparkling')],
    ['beer', t('travelForm.drinkBeer')],
    ['rakija', t('travelForm.drinkRakija')],
    ['vodka', t('travelForm.drinkVodka')],
    ['gin', t('travelForm.drinkGin')],
    ['whisky', t('travelForm.drinkWhisky')],
    ['rum', t('travelForm.drinkRum')],
    ['tequila', t('travelForm.drinkTequila')]
  ];

  const transferOptions = (field, otherField, testPrefix) => (
    <RadioGroup value={person[field]} onValueChange={(v) => set(field, v)} className="mt-2 space-y-2">
      {[['yes', t('travelForm.transferYes')], ['own', t('travelForm.transferNo')], ['other', t('travelForm.transferOther')]].map(([val, label]) => (
        <div key={val} className="flex items-center space-x-2">
          <RadioGroupItem value={val} id={`${testPrefix}-${val}-${index}`} data-testid={`${testPrefix}-${val}-${index}`} />
          <Label htmlFor={`${testPrefix}-${val}-${index}`} className="blue-text font-normal cursor-pointer">{label}</Label>
        </div>
      ))}
      {person[field] === 'other' && (
        <Input
          value={person[otherField]}
          onChange={(e) => set(otherField, e.target.value)}
          placeholder={t('travelForm.transferOtherPlaceholder')}
          className="border-2 border-primary cream-bg"
          data-testid={`${testPrefix}-other-input-${index}`}
        />
      )}
    </RadioGroup>
  );

  const showSameTravel = index > 0 && firstPersonName;

  return (
    <Card className="border-2 border-primary cream-bg" data-testid={`travel-person-card-${index}`}>
      <CardHeader className="pb-2">
        <CardTitle className="font-serif text-2xl blue-text" data-testid={`travel-person-title-${index}`}>
          {person.guestName || `${t('travelForm.person')} ${index + 1}`}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Contact */}
        <div>
          <Label className="blue-text">{t('travelForm.phone')} *</Label>
          <Input
            value={person.phone}
            onChange={(e) => set('phone', e.target.value)}
            placeholder="+382 ..."
            className="border-2 border-primary cream-bg mt-1"
            data-testid={`phone-input-${index}`}
          />
        </div>

        {/* Travel Details */}
        <SectionTitle>{t('travelForm.travelDetails')}</SectionTitle>

        {showSameTravel && (
          <div className="flex items-center space-x-2 p-3 border border-primary rounded-sm bg-blue-50/40">
            <Checkbox
              id={`same-travel-${index}`}
              checked={person.sameTravelAsFirst}
              onCheckedChange={(checked) => set('sameTravelAsFirst', !!checked)}
              data-testid={`same-travel-checkbox-${index}`}
            />
            <Label htmlFor={`same-travel-${index}`} className="blue-text font-medium cursor-pointer">
              {t('travelForm.sameTravel')} {firstPersonName}
            </Label>
          </div>
        )}

        {!person.sameTravelAsFirst && (
          <>
            <div>
              <Label className="blue-text">{t('travelForm.travelModeQ')} *</Label>
              <RadioGroup value={person.travelMode} onValueChange={(v) => set('travelMode', v)} className="mt-2 space-y-2">
                {[['flight', t('travelForm.travelModeFlight')], ['other', t('travelForm.travelModeOther')]].map(([val, label]) => (
                  <div key={val} className="flex items-center space-x-2">
                    <RadioGroupItem value={val} id={`travel-mode-${val}-${index}`} data-testid={`travel-mode-${val}-${index}`} />
                    <Label htmlFor={`travel-mode-${val}-${index}`} className="blue-text font-normal cursor-pointer">{label}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {person.travelMode === 'other' ? (
              <div className="md:w-1/2">
                <DatePickerField
                  value={person.arrivalDate}
                  onChange={(v) => set('arrivalDate', v)}
                  label={t('travelForm.arrivalDay')}
                  testId={`arrival-date-btn-${index}`}
                />
              </div>
            ) : (
              <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FlightSection index={index} person={person} set={set} label={t('travelForm.arrival')} prefix="arrival" />
              <FlightSection index={index} person={person} set={set} label={t('travelForm.departure')} prefix="departure" />
            </div>

            <div>
              <Label className="blue-text">{t('travelForm.transferArrivalQ')} *</Label>
              {transferOptions('transferArrival', 'transferArrivalOther', 'transfer-arrival')}
            </div>
            <div>
              <Label className="blue-text">{t('travelForm.transferDepartureQ')} *</Label>
              {transferOptions('transferDeparture', 'transferDepartureOther', 'transfer-departure')}
            </div>
              </>
            )}

            {/* Hotel */}
            <div>
              <Label className="blue-text">{t('travelForm.hotelQ')} *</Label>
              <Input
                value={person.hotel}
                onChange={(e) => set('hotel', e.target.value)}
                placeholder={t('travelForm.hotelPlaceholder')}
                className="border-2 border-primary cream-bg mt-1"
                data-testid={`hotel-input-${index}`}
              />
            </div>
          </>
        )}

        {/* Menu */}
        <SectionTitle>{t('travelForm.menuTitle')}</SectionTitle>
        <div>
          <Label className="blue-text">{t('travelForm.coldAppetizerQ')} *</Label>
          <RadioGroup value={person.coldAppetizer} onValueChange={(v) => set('coldAppetizer', v)} className="mt-2 space-y-2">
            {[['montenegrin', t('travelForm.coldMontenegrin')], ['salmon', t('travelForm.coldSalmon')], ['vegan', t('travelForm.coldVegan')]].map(([val, label]) => (
              <div key={val} className="flex items-start space-x-2">
                <RadioGroupItem value={val} id={`cold-${val}-${index}`} className="mt-1" data-testid={`cold-${val}-${index}`} />
                <Label htmlFor={`cold-${val}-${index}`} className="blue-text font-normal cursor-pointer leading-snug">{label}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>
        <div>
          <Label className="blue-text">{t('travelForm.mainCourseQ')} *</Label>
          <RadioGroup value={person.mainCourse} onValueChange={(v) => set('mainCourse', v)} className="mt-2 space-y-2">
            {[['monkfish', t('travelForm.mainMonkfish')], ['beef', t('travelForm.mainBeef')], ['vegan', t('travelForm.mainVegan')]].map(([val, label]) => (
              <div key={val} className="flex items-start space-x-2">
                <RadioGroupItem value={val} id={`main-${val}-${index}`} className="mt-1" data-testid={`main-${val}-${index}`} />
                <Label htmlFor={`main-${val}-${index}`} className="blue-text font-normal cursor-pointer leading-snug">{label}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>
        <div>
          <Label className="blue-text">{t('travelForm.allergiesQ')}</Label>
          <div className="mt-2 space-y-2">
            {allergyOptions.map(([val, label]) => (
              <div key={val} className="flex items-center space-x-2">
                <Checkbox
                  id={`allergy-${val}-${index}`}
                  checked={(person.allergies || []).includes(val)}
                  onCheckedChange={() => toggleList('allergies', val)}
                  data-testid={`allergy-${val}-${index}`}
                />
                <Label htmlFor={`allergy-${val}-${index}`} className="blue-text font-normal cursor-pointer">{label}</Label>
              </div>
            ))}
            {(person.allergies || []).includes('other') && (
              <Input
                value={person.allergiesOther}
                onChange={(e) => set('allergiesOther', e.target.value)}
                placeholder={t('travelForm.allergyOtherPlaceholder')}
                className="border-2 border-primary cream-bg"
                data-testid={`allergy-other-input-${index}`}
              />
            )}
          </div>
          <p className="text-xs blue-text opacity-70 mt-2 italic">{t('travelForm.flowerAllergyNote')}</p>
        </div>

        {/* Drinks */}
        <div>
          <Label className="blue-text">{t('travelForm.drinksQ')}</Label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {drinkOptions.map(([val, label]) => (
              <div key={val} className="flex items-center space-x-2">
                <Checkbox
                  id={`drink-${val}-${index}`}
                  checked={(person.drinks || []).includes(val)}
                  onCheckedChange={() => toggleList('drinks', val)}
                  data-testid={`drink-${val}-${index}`}
                />
                <Label htmlFor={`drink-${val}-${index}`} className="blue-text font-normal cursor-pointer">{label}</Label>
              </div>
            ))}
          </div>
        </div>

        {/* Beauty Services */}
        <SectionTitle>{t('travelForm.beautyTitle')}</SectionTitle>
        <div>
          <Label className="blue-text">{t('travelForm.beautyQ')} *</Label>
          <RadioGroup value={person.beautyService} onValueChange={(v) => set('beautyService', v)} className="mt-2 space-y-2">
            {[['makeup', t('travelForm.beautyMakeup')], ['hair', t('travelForm.beautyHair')], ['full_look', t('travelForm.beautyFullLook')], ['none', t('travelForm.beautyNone')]].map(([val, label]) => (
              <div key={val} className="flex items-center space-x-2">
                <RadioGroupItem value={val} id={`beauty-${val}-${index}`} data-testid={`beauty-${val}-${index}`} />
                <Label htmlFor={`beauty-${val}-${index}`} className="blue-text font-normal cursor-pointer">{label}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        {/* Comments */}
        <div>
          <Label className="blue-text">{t('travelForm.commentsQ')}</Label>
          <Textarea
            value={person.comments}
            onChange={(e) => set('comments', e.target.value)}
            placeholder={t('travelForm.commentsPlaceholder')}
            className="border-2 border-primary cream-bg mt-1"
            data-testid={`comments-input-${index}`}
          />
        </div>
      </CardContent>
    </Card>
  );
};
